import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cashRegisterService, CashRegisterCreate, CashRegisterClose, CashRegisterFilters, CashWithdrawalCreate } from '@/services/cashRegister';

export function useCashRegisterStatus(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['cash-register', 'status'],
    queryFn: cashRegisterService.getStatus,
    refetchInterval: 5000, // Refrescar cada 5 segundos para sincronizar entre usuarios
    staleTime: 0, // Siempre considerar datos como stale
    refetchOnWindowFocus: true, // Refrescar cuando la ventana obtiene foco
    enabled: options?.enabled ?? true,
  });
}

export function useCashRegisters(filters?: CashRegisterFilters) {
  return useQuery({
    queryKey: ['cash-register', 'list', filters],
    queryFn: () => cashRegisterService.getRegisters(filters),
  });
}

export function useCashRegister(id: number | null) {
  return useQuery({
    queryKey: ['cash-register', id],
    queryFn: () => cashRegisterService.getRegister(id!),
    enabled: !!id,
  });
}

export function useOpenCashRegister() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CashRegisterCreate) => cashRegisterService.openRegister(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-register'] });
    },
  });
}

export function useCloseCashRegister() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CashRegisterClose }) =>
      cashRegisterService.closeRegister(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-register'] });
    },
  });
}

export function useReopenCashRegister() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => cashRegisterService.reopenRegister(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-register'] });
    },
  });
}

export function useProfitHistory(days: number = 30) {
  return useQuery({
    queryKey: ['cash-register', 'profit-history', days],
    queryFn: () => cashRegisterService.getProfitHistory(days),
  });
}

// === Hooks de Salidas de Caja ===

export function useCashWithdrawals(cashRegisterId?: number, enabled = true) {
  return useQuery({
    queryKey: ['cash-register', 'withdrawals', cashRegisterId],
    queryFn: () => cashRegisterService.getWithdrawals(cashRegisterId),
    enabled,
    refetchOnWindowFocus: true,
  });
}

export function useCreateCashWithdrawal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CashWithdrawalCreate) => cashRegisterService.createWithdrawal(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-register'] });
    },
  });
}

export function useDeleteCashWithdrawal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => cashRegisterService.deleteWithdrawal(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-register'] });
    },
  });
}
